import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/Common";
import { toast } from "sonner";
import { BookOpen, CheckCircle2, Clock, XCircle, Undo2 } from "lucide-react";
import { useDataSync } from "../../lib/realtime";

const ASSESS_BADGE = {
  on_time: { label: "On time", cls: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  late: { label: "Late work", cls: "bg-amber-50 text-amber-700", icon: Clock },
  undone: { label: "Undone", cls: "bg-rose-50 text-rose-700", icon: XCircle },
};

export default function StudentHomework() {
  const [list, setList] = useState([]);
  const [subs, setSubs] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [h, s] = await Promise.all([api.get("/homework"), api.get("/homework/my-submissions")]);
      setList(h.data);
      const map = {};
      s.data.forEach((x) => { map[x.homework_id] = x; });
      setSubs(map);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useDataSync(["homework", "homework_submissions"], () => load());

  const mark = async (hw, done) => {
    setBusy(hw.id);
    try {
      await api.post(`/homework/${hw.id}/mark`, { done });
      toast.success(done ? "Marked as done — great job! 🎉" : "Marked as not done");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <PageHeader title="Homework" subtitle="Mark each task done when you finish — your teacher reviews and grades your discipline." />
      {list.length === 0 ? (
        <EmptyState title="Nothing here yet" desc="Your teachers will share homework here." icon={BookOpen} />
      ) : (
        <div className="space-y-3">
          {list.map((h) => {
            const sub = subs[h.id];
            const reviewed = !!sub?.assessment;
            const done = sub?.status === "done";
            const overdue = h.due_date && today > h.due_date && !done && !reviewed;
            const badge = reviewed ? ASSESS_BADGE[sub.assessment] : null;
            return (
              <div key={h.id} className="bg-white border border-slate-200 rounded-xl p-4 ym-card-hover" data-testid={`student-hw-${h.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 grid place-items-center shrink-0">
                    <BookOpen className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-heading font-bold text-slate-900">{h.title}</h3>
                      {badge && (
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.cls}`} data-testid={`hw-assessment-${h.id}`}>
                          <badge.icon className="h-3 w-3" /> Teacher: {badge.label}
                        </span>
                      )}
                      {!reviewed && done && (
                        <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${sub.timeliness === "late" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`hw-status-${h.id}`}>
                          Done {sub.timeliness === "late" ? "(late)" : "(on time)"}
                        </span>
                      )}
                      {overdue && (
                        <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">Overdue</span>
                      )}
                    </div>
                    {h.description && <p className="text-sm text-slate-600 mt-0.5">{h.description}</p>}
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                      {h.due_date && <span>Due {h.due_date}</span>}
                      {h.file_url && <a href={h.file_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">📄 Download</a>}
                      {h.video_url && <a href={h.video_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">🎥 Watch</a>}
                    </div>
                    {reviewed && sub.remarks && (
                      <p className="mt-2 text-xs bg-blue-50 border border-blue-100 text-slate-700 rounded-lg px-3 py-2" data-testid={`hw-remarks-view-${h.id}`}>
                        <b className="text-blue-700">Teacher's remark:</b> {sub.remarks}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0">
                    {reviewed ? null : done ? (
                      <button onClick={() => mark(h, false)} disabled={busy === h.id}
                        data-testid={`hw-mark-undone-${h.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60">
                        <Undo2 className="h-3.5 w-3.5" /> Not done
                      </button>
                    ) : (
                      <button onClick={() => mark(h, true)} disabled={busy === h.id}
                        data-testid={`hw-mark-done-${h.id}`}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-60">
                        <CheckCircle2 className="h-4 w-4" /> Mark done
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
